import type { FormikHelpers } from 'formik';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { CloudUploadScope } from 'api/apiCloudflare';
import SolarIcon from 'components/icons/SolarIcon';
import spinnerTitle from 'components/others/spinnerTitle';
import type { ProductEditType } from '../../types';
import ProductPhotoMark from '../../ProductRowItems/ProductPhotoMark';
import useProductPhoto from '../model/useProductPhoto';
import CropPhoto from './cropPhoto';

interface ProductFormPhotoProps {
    setFieldValue: FormikHelpers<ProductEditType>['setFieldValue'];
    values: ProductEditType;
    ensureSaved: () => Promise<CloudUploadScope | false>;
    fetchProducts: (force?: boolean) => Promise<unknown>;
}

const ProductFormPhoto = ({ setFieldValue, values, ensureSaved, fetchProducts }: ProductFormPhotoProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const {
        fileRef,
        busy,
        busyAction,
        pickingId,
        candidates,
        loadingSaved,
        hideAutoPhoto,
        cropSrc,
        canEdit,
        savedUrl,
        shownUrl,
        productName,
        canSearch,
        photoSearchMinName,
        openPicker,
        handleChange,
        handleRemove,
        handleSearch,
        handlePick,
        handleOpenCrop,
        handleCropBack,
        handleCropApply,
    } = useProductPhoto({ setFieldValue, values, ensureSaved, fetchProducts });

    const actionContent = (icon: string, title: string, spinning: boolean, spinningTitle: string) => (
        <>
            {spinning ? null : <SolarIcon name={icon} className="flex-shrink-0" aria-hidden="true" />}
            {spinnerTitle(title, spinning, spinningTitle)}
        </>
    );

    return (
        <div className="pb-4">
            <div className="mb-3">
                <div className={`text-break ${productName ? 'text-body fw-medium' : 'text-muted'}`}>
                    {productName || t('messPhotoNeedName')}
                </div>
                {canEdit && !canSearch ? (
                    <div className="small text-muted mt-1">{t('messPhotoNameMin', { min: photoSearchMinName })}</div>
                ) : null}
            </div>
            <div className="product-photo-panel">
                <input
                    ref={fileRef}
                    type="file"
                    accept=".png,.jpeg,.jpg,.webp,.gif,.svg"
                    className="d-none"
                    onChange={handleChange}
                />
                <div className="product-photo-panel__slot">
                    <ProductPhotoMark
                        large
                        name={values.prodName}
                        src={shownUrl}
                        onClick={canEdit ? openPicker : undefined}
                    />
                    {canEdit && savedUrl ? (
                        <Button
                            type="button"
                            variant="light"
                            className="product-photo-panel__crop"
                            disabled={busy || Boolean(cropSrc)}
                            title={t('btnCropPhoto')}
                            aria-label={t('btnCropPhoto')}
                            onClick={handleOpenCrop}
                        >
                            {busyAction === 'crop' && !cropSrc ? (
                                <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                            ) : (
                                <SolarIcon name="crop" aria-hidden="true" />
                            )}
                        </Button>
                    ) : null}
                    {canEdit && savedUrl ? (
                        <Button
                            type="button"
                            variant="light-danger"
                            className="product-photo-panel__remove"
                            disabled={busy}
                            title={t('btnRemovePhoto')}
                            aria-label={t('btnRemovePhoto')}
                            onClick={handleRemove}
                        >
                            {busyAction === 'remove' ? (
                                <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                            ) : (
                                <SolarIcon name="trash" aria-hidden="true" />
                            )}
                        </Button>
                    ) : null}
                </div>
                {canEdit ? (
                    <div className="product-photo-panel__toolbar">
                        <Button type="button" size="sm" variant="info" disabled={busy} onClick={openPicker}>
                            {actionContent(
                                'cloud-upload',
                                t('btnUploadPhoto'),
                                busyAction === 'upload',
                                t('common:buttons.uploading'),
                            )}
                        </Button>
                        {hideAutoPhoto ? null : (
                            <Button
                                type="button"
                                size="sm"
                                variant="light"
                                className="product-photo-ai-btn"
                                disabled={busy || loadingSaved || !canSearch}
                                onClick={handleSearch}
                            >
                                {actionContent(
                                    'stars',
                                    t('btnAutoPhoto'),
                                    busyAction === 'search',
                                    t('btnSearchingPhoto'),
                                )}
                            </Button>
                        )}
                    </div>
                ) : null}
            </div>
            {canEdit && cropSrc ? (
                <CropPhoto src={cropSrc} busy={busy} onBack={handleCropBack} onApply={handleCropApply} />
            ) : null}
            {canEdit && !cropSrc && loadingSaved ? (
                <div className="product-photo-candidates product-photo-candidates--loading" aria-busy="true">
                    <Spinner animation="border" variant="primary" role="status" aria-hidden="true" />
                </div>
            ) : null}
            {canEdit && !cropSrc && !loadingSaved && candidates.length ? (
                <div className="product-photo-candidates">
                    {candidates.map((item) => {
                        const applying = pickingId === item.id;
                        return (
                            <div
                                key={item.id}
                                className={`product-photo-candidates__item${applying ? ' is-applying' : ''}`}
                            >
                                <button
                                    type="button"
                                    className="product-photo-candidates__pick"
                                    disabled={busy}
                                    title={item.title}
                                    onClick={() => handlePick(item.id)}
                                >
                                    <img src={item.thumbUrl} alt={item.title} />
                                </button>
                                {applying ? (
                                    <span className="product-photo-candidates__busy">
                                        <Spinner animation="border" variant="primary" size="sm" role="status" aria-hidden="true" />
                                    </span>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};

export default ProductFormPhoto;
